import { useState } from 'react';
import { useLazyDownloadFileQuery, useLazyDownloadArchiveQuery } from '../features/fileSystem/filesStorage';
import { FileItem } from '../types/interfaces/filesStorage';
import {
  isDownloadArchiveError,
  isDownloadArchiveBlob,
  isDownloadArchiveUrl
} from '../types/interfaces';

interface DownloadState {
  downloadingItems: Set<string>;
  downloadProgress: Record<string, number | undefined>;
  downloadErrors: Record<string, string | undefined>;
}

interface DownloadResult {
  success: boolean;
  message: string;
  error?: string;
}

export const useDownloadManager = () => {
  const [state, setState] = useState<DownloadState>({
    downloadingItems: new Set(),
    downloadProgress: {},
    downloadErrors: {},
  });

  const [triggerDownloadFile] = useLazyDownloadFileQuery();
  const [triggerDownloadArchive] = useLazyDownloadArchiveQuery();

  const getItemKey = (item: FileItem) => `${item.fsource}-${item.fname}`;

  const isDownloading = (item: FileItem) => state.downloadingItems.has(getItemKey(item));

  const getDownloadProgress = (item: FileItem) => state.downloadProgress[getItemKey(item)] || 0;

  const getDownloadError = (item: FileItem) => state.downloadErrors[getItemKey(item)];

  const setDownloading = (item: FileItem, downloading: boolean) => {
    const itemKey = getItemKey(item);
    setState(prev => {
      const newDownloadingItems = new Set(prev.downloadingItems);
      if (downloading) {
        newDownloadingItems.add(itemKey);
      } else {
        newDownloadingItems.delete(itemKey);
      }

      return {
        ...prev,
        downloadingItems: newDownloadingItems,
        downloadProgress: downloading
          ? { ...prev.downloadProgress, [itemKey]: 0 }
          : { ...prev.downloadProgress, [itemKey]: undefined },
        downloadErrors: downloading
          ? { ...prev.downloadErrors, [itemKey]: undefined }
          : { ...prev.downloadErrors, [itemKey]: undefined },
      };
    });
  };

  const setDownloadProgress = (item: FileItem, progress: number) => {
    const itemKey = getItemKey(item);
    setState(prev => ({
      ...prev,
      downloadProgress: { ...prev.downloadProgress, [itemKey]: progress },
    }));
  };

  const setDownloadError = (item: FileItem, error: string) => {
    const itemKey = getItemKey(item);
    setState(prev => ({
      ...prev,
      downloadErrors: { ...prev.downloadErrors, [itemKey]: error },
    }));
  };

  const triggerDownload = (item: FileItem, filename: string) => {
    setDownloadProgress(item, 100);
    setTimeout(() => {
      setDownloading(item, false);
    }, 500);
  };

    const downloadFile = async (item: FileItem): Promise<DownloadResult> => {
    if (isDownloading(item)) {
      return { success: false, message: 'Download already in progress' };
    }

    setDownloading(item, true);
    setDownloadProgress(item, 10);

    // Simulate progress for better UX
    let currentProgress = 10;
    const progressInterval = setInterval(() => {
      currentProgress = Math.min(currentProgress + 10, 90);
      setDownloadProgress(item, currentProgress);
    }, 200);

    try {
      const response = await triggerDownloadFile({
        source: item.fsource,
        fileName: item.fname,
      }).unwrap();

      clearInterval(progressInterval);
      setDownloadProgress(item, 95);

      if (response && response.file_urls && response.file_urls.length > 0) {
        const link = document.createElement("a");
        link.href = response.file_urls[0];
        link.download = item.fname;
        document.body.appendChild(link);
        link.click();
        link.remove();

        triggerDownload(item, item.fname);
        return {
          success: true,
          message: `File "${item.fname}" downloaded successfully`
        };
      } else {
        const error = 'No download URL available';
        setDownloadError(item, error);
        setDownloading(item, false);
        return { success: false, message: error };
      }
    } catch (error) {
      clearInterval(progressInterval);
      const errorMessage = error instanceof Error ? error.message : 'Download failed';
      setDownloadError(item, errorMessage);
      setDownloading(item, false);
      return { success: false, message: errorMessage, error: errorMessage };
    }
  };

    const downloadArchive = async (item: FileItem): Promise<DownloadResult> => {
    if (isDownloading(item)) {
      return { success: false, message: 'Download already in progress' };
    }

    setDownloading(item, true);
    setDownloadProgress(item, 10);

    // Simulate progress for better UX
    let currentProgress = 10;
    const progressInterval = setInterval(() => {
      currentProgress = Math.min(currentProgress + 10, 90);
      setDownloadProgress(item, currentProgress);
    }, 200);

    try {
      const response = await triggerDownloadArchive({
        source: item.fsource,
        fileName: item.fname,
      }).unwrap();

      clearInterval(progressInterval);
      setDownloadProgress(item, 95);

      // Handle different response types
      if (isDownloadArchiveUrl(response)) {
        // AWS Lambda case: download from presigned S3 URL
        const link = document.createElement('a');
        link.href = response.download_url;
        link.style.display = 'none';
        link.download = response.filename || `${item.fname}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        triggerDownload(item, response.filename || `${item.fname}.zip`);
        return {
          success: true,
          message: `Folder "${item.fname}" download started`
        };
      } else if (isDownloadArchiveBlob(response)) {
        // Local development case: handle blob response
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${item.fname}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        triggerDownload(item, `${item.fname}.zip`);
        return {
          success: true,
          message: `Folder "${item.fname}" downloaded successfully`
        };
      } else if (isDownloadArchiveError(response)) {
        const error = `Failed to download folder: ${response.error}`;
        setDownloadError(item, error);
        setDownloading(item, false);
        return { success: false, message: error };
      } else {
        // Fallback for unexpected response format
        const error = 'Unexpected response format for folder download';
        setDownloadError(item, error);
        setDownloading(item, false);
        return { success: false, message: error };
      }
    } catch (error) {
      clearInterval(progressInterval);
      const errorMessage = error instanceof Error ? error.message : 'Download failed';
      setDownloadError(item, errorMessage);
      setDownloading(item, false);
      return { success: false, message: errorMessage, error: errorMessage };
    }
  };

  const downloadItem = async (item: FileItem): Promise<DownloadResult> => {
    if (item.ftype === "folder") {
      return downloadArchive(item);
    } else {
      return downloadFile(item);
    }
  };

  return {
    // State
    downloadingItems: state.downloadingItems,
    downloadProgress: state.downloadProgress,
    downloadErrors: state.downloadErrors,

    // Methods
    isDownloading,
    getDownloadProgress,
    getDownloadError,
    downloadItem,
    downloadFile,
    downloadArchive,
  };
};
